import { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { API_BASE_URL } from '../lib/api'

// BRIEF-B8: the owner chooses who is watched. Mounted on
// /smb/consent-complete?session=… only, after the Allow has been recorded and
// before the staff-notice step.
//
// Routes (backend app/routers/smb_onboarding.py, read-only here):
//   POST /api/v1/smb-onboarding/{session}/seats/propose  (no body)
//     200 → { state: 'seats_proposed', seats: [{ upn, display_name }] }
//     400 → { detail: '<sentence>' }
//   POST /api/v1/smb-onboarding/{session}/seats/select
//     body { upns: string[] } (1..1000)
//     200 → { outcome: 'completed', state, seats_selected, ... }
//     400 → { detail: '<sentence>' }
//
// Doctrine:
// - Show exactly what propose returns: name and address. The route carries no
//   suggested tick, no shared-mailbox mark and no guest mark, so none is shown
//   and every person starts unticked — a tick of our own would be a
//   suggestion of our own.
// - Select posts exactly the addresses the owner ticked, nothing else.
// - A 400 carries a sentence in `detail`: show that sentence as written.
// - After select succeeds, go to the portal's staff-notice page
//   (/client/staff-notice, BRIEF-B10, same origin as the app, under the
//   portal's own sign-in). It is not reimplemented here.
// - Unauthenticated by design (the session token is the capability): plain
//   fetch, never the staff axios client.

type Seat = { upn: string; display_name: string }

const FALLBACK = 'That did not go through. Please try again in a moment.'

export const STAFF_NOTICE_PAGE = '/client/staff-notice'

async function sentenceFrom(res: Response): Promise<string> {
  if (res.status !== 400) return FALLBACK
  try {
    const body = await res.json()
    return typeof body?.detail === 'string' && body.detail ? body.detail : FALLBACK
  } catch {
    return FALLBACK
  }
}

// States after select has gone through (backend services/smb_onboarding.py
// SESSION_STATES). Read from the public status route, never guessed.
const PAST_SEATS = new Set(['pending_subscription', 'subscription_active'])

async function alreadyChosen(session: string): Promise<boolean> {
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/v1/smb-onboarding/${encodeURIComponent(session)}`,
    )
    if (!res.ok) return false
    const body = await res.json()
    return typeof body?.state === 'string' && PAST_SEATS.has(body.state)
  } catch {
    return false
  }
}

function seatsUrl(session: string, step: 'propose' | 'select'): string {
  return `${API_BASE_URL}/api/v1/smb-onboarding/${encodeURIComponent(session)}/seats/${step}`
}

export function SmbSeatsStep({
  session,
  onSelected = () => window.location.assign(STAFF_NOTICE_PAGE),
}: {
  session: string
  onSelected?: () => void
}) {
  const [seats, setSeats] = useState<Seat[] | null>(null)
  const [ticked, setTicked] = useState<Set<string>>(() => new Set())
  const [refusal, setRefusal] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  // True only when the status route says select already went through (a
  // reload, a reopened link, or a select whose answer was lost).
  const [chosen, setChosen] = useState(false)
  const [attempt, setAttempt] = useState(0)
  // Effects can re-fire (StrictMode remount); propose runs once, and its
  // answer is dropped if the page has gone by the time it lands.
  const startedRef = useRef(0)
  const mountedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (startedRef.current === attempt + 1) return
    startedRef.current = attempt + 1
    async function propose() {
      try {
        const res = await fetch(seatsUrl(session, 'propose'), { method: 'POST' })
        if (!res.ok) {
          const sentence = await sentenceFrom(res)
          const past = await alreadyChosen(session)
          if (!mountedRef.current) return
          if (past) setChosen(true)
          else setRefusal(sentence)
          return
        }
        const body = await res.json()
        if (mountedRef.current) setSeats(Array.isArray(body?.seats) ? body.seats : [])
      } catch {
        if (mountedRef.current) setRefusal(FALLBACK)
      }
    }
    void propose()
  }, [session, attempt])

  function retry() {
    setRefusal(null)
    setSeats(null)
    setAttempt((n) => n + 1)
  }

  function toggle(upn: string) {
    setTicked((prev) => {
      const next = new Set(prev)
      if (next.has(upn)) next.delete(upn)
      else next.add(upn)
      return next
    })
  }

  async function confirm() {
    if (!seats) return
    setSending(true)
    setRefusal(null)
    try {
      const upns = seats.map((s) => s.upn).filter((upn) => ticked.has(upn))
      const res = await fetch(seatsUrl(session, 'select'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upns }),
      })
      if (res.ok) {
        onSelected()
        return
      }
      const sentence = await sentenceFrom(res)
      if (await alreadyChosen(session)) {
        setChosen(true)
        setSending(false)
        return
      }
      setRefusal(sentence)
    } catch {
      setRefusal(FALLBACK)
    }
    setSending(false)
  }

  return (
    <section
      aria-labelledby="seats-heading"
      className="mt-6 pt-6 border-t border-gray-200 text-left"
    >
      <h2 id="seats-heading" className="text-base font-serif font-bold text-navy">
        Choose who is watched
      </h2>
      <p className="text-sm text-gray-500 mt-1">
        These are the people your administrator’s grant lets us see. Tick each
        person you want watched, then confirm. Next you will tell your staff.
      </p>

      {chosen && (
        <div className="mt-4" data-testid="seats-already-chosen">
          <p className="text-sm text-navy break-words">
            The people to watch have already been chosen for this sign-up.
          </p>
          <button
            type="button"
            onClick={() => onSelected()}
            className="w-full inline-flex items-center justify-center mt-4 tap-min px-5 py-2.5 rounded-lg bg-navy text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
          >
            Go on to tell your staff
          </button>
        </div>
      )}

      {seats === null && !refusal && !chosen && (
        <p className="text-sm text-gray-500 mt-4 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          Fetching the people…
        </p>
      )}

      {seats && seats.length > 0 && (
        <ul className="mt-4 space-y-2" aria-label="Proposed people">
          {seats.map((seat) => (
            <li key={seat.upn}>
              <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer min-w-0">
                <input
                  type="checkbox"
                  className="mt-1 shrink-0"
                  checked={ticked.has(seat.upn)}
                  onChange={() => toggle(seat.upn)}
                  disabled={sending}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-navy break-words">
                    {seat.display_name}
                  </span>
                  <span className="block text-xs text-gray-500 break-all">
                    {seat.upn}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {seats && seats.length === 0 && (
        <p className="text-sm text-navy mt-4 break-words" data-testid="seats-none">
          Your administrator’s grant listed no active people to watch.
        </p>
      )}

      {refusal && (
        <p role="alert" className="text-sm text-danger mt-4 break-words">
          {refusal}
        </p>
      )}

      {((refusal && seats === null) || (seats && seats.length === 0)) && (
        <button
          type="button"
          onClick={retry}
          className="mt-3 text-sm font-medium text-navy underline"
        >
          Try again
        </button>
      )}

      {seats && seats.length > 0 && (
        <button
          type="button"
          onClick={() => void confirm()}
          disabled={sending || ticked.size === 0}
          className="w-full inline-flex items-center justify-center mt-4 tap-min px-5 py-2.5 rounded-lg bg-navy text-white text-sm font-medium disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
        >
          {sending ? 'Confirming…' : 'Confirm who is watched'}
        </button>
      )}
    </section>
  )
}
